import React, { useContext } from 'react';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import KonteringContext from '../../../../../../../_Common/Components/KonteringManager/KonteringContext';
import {
  tillaggsinfoIsKomponent,
  Tillaggsinfokod,
} from '../../../Kontering/Components/Tillaggsinfo/Utils/TillaggsinfoUtils';
import { GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All } from '../../Queries/_QueryTypes/GetKonteringHistory';

interface IProps {
  additionalInfo: GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All | null;
  billDate: string;
}

const AdditionalInfoHistory: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const konteringContext = useContext(KonteringContext);
  const kodplanContext = useContext(KodplanContext);

  const additionalInfo = props.additionalInfo;
  if (!additionalInfo) {
    return <>Det verkar inte finnas tilläggsinformation</>;
  }

  const isFI = additionalInfo.TILLAGGSINFO_KOD === Tillaggsinfokod.FI;

  const isNew = Boolean(additionalInfo.AR_NY);
  const isKomponent = tillaggsinfoIsKomponent({
    benamning: '',
    kod: additionalInfo.TILLAGGSINFO_KOD ?? '',
    aktiv: true,
    falt: [],
  });
  const baskonto = additionalInfo._Associations?.Kontodel1;
  const billDate = new Date(props.billDate);

  const kodplanID = kodplanContext.availableKodplanList.filter(kodplan => {
    const kodFromDate = new Date(kodplan.frombokfar);
    const kodTomDate = new Date(kodplan.tombokfar);
    return kodFromDate <= billDate && kodTomDate >= billDate;
  })?.[0]?.id;

  const tillaggsinfo = baskonto
    ? konteringContext.konteringUtils.getKontodelTillaggsinfo(
        kodplanID,
        baskonto?.id
      )
    : null;

  if (!tillaggsinfo) {
    return <>Det verkar inte finnas någon data kring tilläggsinformation</>;
  }

  const komponentbenamningField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_BENAMNING'
  );

  const komponenttypField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_KOMPONENT_ID'
  );

  const avskrivningstidField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_AVSKR_TID'
  );

  const invdatumField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_INV_DATUM'
  );

  const tillgangField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_TILLGANG'
  );

  const omradeField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_OMRADE_ID'
  );

  const avskrivningskontoField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_AVSKR_KDEL'
  );

  const kommentarField = tillaggsinfo.falt.find(
    field => field.fieldName === 'AR_KOMMENTAR'
  );

  if (isFI) {
    return (
      <EDBox>
        <EDTypography>
          <EDTypography bold component="span">
            {'Benämning förbrukningsinventarie: '}
          </EDTypography>
          {additionalInfo?.AR_FI_BENAMNING}
        </EDTypography>
        <EDTypography>
          <EDTypography bold component="span">
            {'Inköpsdatum: '}
          </EDTypography>
          {additionalInfo?.AR_FI_DATUM}
        </EDTypography>
        <EDTypography>
          <EDTypography bold component="span">
            {'Kategori: '}
          </EDTypography>
          {additionalInfo._Associations?.FIKategori?.KodBenamning?.replace(
            ':',
            ' -'
          )}
        </EDTypography>
        <EDTypography>
          <EDTypography bold component="span">
            {'Område (Plats): '}
          </EDTypography>
          {additionalInfo._Associations?.FIOmrade?.BENAMNING}
        </EDTypography>
        <EDTypography>
          <EDTypography bold component="span">
            {'Byggnad: '}
          </EDTypography>
          {additionalInfo._Associations?.FIByggnad?.BENAMNING}
        </EDTypography>
        <EDTypography>
          <EDTypography bold component="span">
            {'Kommentar: '}
          </EDTypography>
          {additionalInfo?.AR_FI_KOMMENTAR}
        </EDTypography>
      </EDBox>
    );
  }

  return (
    <EDBox>
      {komponentbenamningField?.aktiv && (
        <EDTypography>
          <EDTypography bold component="span">
            {isKomponent ? 'Komponentbenämning: ' : 'Benämning investering: '}
          </EDTypography>
          {additionalInfo?.AR_BENAMNING}
        </EDTypography>
      )}
      {komponenttypField?.aktiv && isKomponent && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Komponenttyp: '}
          </EDTypography>
          {additionalInfo?._Associations?.Komponenttyp?.BENAMNING}
        </EDTypography>
      )}
      {avskrivningstidField?.aktiv && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Avskrivningstid: '}
          </EDTypography>
          {additionalInfo.AR_AVSKR_TID}
        </EDTypography>
      )}
      {invdatumField?.aktiv && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Datum (tagen i bruk): '}
          </EDTypography>
          {additionalInfo?.AR_INV_DATUM}
        </EDTypography>
      )}
      <EDTypography>
        <EDTypography bold component="span">
          {isKomponent
            ? 'Befintlig byggnad: '
            : 'Befintlig maskin/inventarie: '}
        </EDTypography>
        {isNew ? 'Nej' : 'Ja'}
      </EDTypography>
      {tillgangField?.aktiv && !isNew && (
        <>
          <EDTypography>
            <EDTypography bold component="span">
              {isKomponent ? 'Byggnad: ' : 'Anl.tillgång: '}
            </EDTypography>
            {additionalInfo._Associations?.Tillgang?.AnlnrBen}
          </EDTypography>
          <EDTypography>
            <EDTypography bold component="span">
              {'Område (Plats): '}
            </EDTypography>
            {`${additionalInfo._Associations?.Tillgang?._Associations?.Plats?.PLATS_KOD} - ${additionalInfo._Associations?.Tillgang?._Associations?.Plats?.BENAMNING}`}
          </EDTypography>
        </>
      )}
      {tillgangField?.aktiv && isNew && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Byggnad: '}
          </EDTypography>
          {additionalInfo.AR_TILLGANG_BEN}
        </EDTypography>
      )}
      {avskrivningskontoField?.aktiv && additionalInfo.AR_NY && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Avskrivs på: '}
          </EDTypography>
          {additionalInfo.AvskrKontostrang ?? ''}
        </EDTypography>
      )}

      {omradeField?.aktiv && additionalInfo.AR_NY && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Område (Plats): '}
          </EDTypography>
          {additionalInfo._Associations?.Omrade?.BENAMNING}
        </EDTypography>
      )}
      {kommentarField?.aktiv && (
        <EDTypography>
          <EDTypography bold component="span">
            {'Kommentar: '}
          </EDTypography>
          {additionalInfo?.AR_KOMMENTAR}
        </EDTypography>
      )}
    </EDBox>
  );
};

export default AdditionalInfoHistory;
